import {
  RowData,
  RowDataObject,
  ExtendedAccountInfo,
  JsonSchema,
  ISelectableOption,
  PrimitiveValueType,
  SetupBaseRequestInfo,
  SetupListNavInfoByDepartment,
  ActiveARRecordMigrationInfo,
  JsonSchemaItems,
  EnumLabels,
} from "../../../types";
import {
  TRACK_RECORD_DATA_STATE_KEYS,
  ROW_DATA_KEYS,
  SCHEMA_TITLES,
  CUSTOM_ROWDATA_FIELDS_TO_ADD,
  NO_RESULTS_DEFAULT_IOPTION,
} from "../constants";
import { getCNumberFromStore } from "../evaluations";
import { convertToOptions } from "./optionHelpers";
import { isNewRecord } from "./rowDataHelpers";
import {
  getCompanyIdByStoreId,
  getDepartmentIdByStore,
  getStoreOptionsFromDetailsAPIResponse,
} from "./storeHelpers";

const getDefaultBeginDate = (): string => {
  const today = new Date();
  today.setFullYear(today.getFullYear() - 5);
  return today.toISOString().split("T")[0]; // '2020-06-24'
};

export const getUpdatedNewEntryRowData = async ({
  schema,
  rowData,
  store,
  department,
  extendedAccountInfo,
  accountName,
  setupBaseRequestInfo,
  setupListNavInfoByDepartment,
}: {
  schema: JsonSchema;
  rowData: RowData;
  store: string;
  department: string;
  extendedAccountInfo: ExtendedAccountInfo[];
  accountName?: string;
  setupBaseRequestInfo: SetupBaseRequestInfo;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
}): Promise<RowData> => {
  const companyId = getCompanyIdByStoreId(store, extendedAccountInfo);
  const departmentId = getDepartmentIdByStore({
    store,
    department,
    extendedAccountInfo,
    setupListNavInfoByDepartment,
  });
  const departmentName =
    setupListNavInfoByDepartment?.[department]?.departmentIds.find(
      (dept) => dept.storeId === store,
    )?.name ?? "";

  const fieldsToUpdate = [
    {
      fields: ["begin_date", "journal_begin_date"],
      value: getDefaultBeginDate(),
    },
    {
      fields: ["company_id", "allocation_company", "source_company_id"],
      value: companyId,
    },
    {
      fields: ["accounting_account", "logon"],
      value: accountName,
    },
    {
      fields: ["departmentId", "department_id"],
      value: departmentId,
    },
    {
      fields: ["department_name"],
      value: departmentName,
    },
    {
      tab: "poAddressTab",
      fields: ["group_addresses"],
      value: [],
    },
    {
      fields: [
        "labor_accounts_cp",
        "parts_accounts_cp",
        "labor_accounts_ip",
        "parts_accounts_ip",
        "labor_accounts_wp",
        "parts_accounts_wp",
      ],
      value: [],
    },
    {
      tab: "poGroupTab",
      fields: ["acc_name"],
      value: accountName,
    },
    {
      fields: ["cnumber"],
      value: await getCNumberFromStore(setupBaseRequestInfo),
    },
    {
      tab: "poTypesTab",
      fields: ["poTypes"],
      value: getPODefaultTypes(rowData, schema),
    },
  ];

  return fieldsToUpdate.reduce((updatedRowData, { tab, fields, value }) => {
    fields.forEach((field) => {
      if (
        schema.properties[field] ||
        CUSTOM_ROWDATA_FIELDS_TO_ADD.includes(`${schema.title}-${field}`)
      ) {
        if (tab) {
          updatedRowData = {
            ...updatedRowData,
            [tab]: {
              ...(updatedRowData[tab] as RowData),
              [field]: value ?? "",
            },
          };
        } else {
          updatedRowData = {
            ...updatedRowData,
            [field]: value ?? "",
          };
        }
      }
    });
    return updatedRowData;
  }, rowData);
};

export const isAPICallRequiredForFetchingRowData = (
  rowData: RowData,
  title: string,
): boolean => {
  // As of now PO Configuration Group is the only setup that requires API call
  if (title !== SCHEMA_TITLES.PO_CONFIGURATION_GROUP) return false;

  const isNewEntryRecord = isNewRecord(rowData);
  // for cloned records id is required to do API call and it will be set while cloning
  const isClonedRecordHasId =
    rowData[TRACK_RECORD_DATA_STATE_KEYS.TRACK_EXISTING_ID] !== undefined;

  return !isNewEntryRecord || (isNewEntryRecord && isClonedRecordHasId);
};

export const getExistingRecordId = (rowData: RowData): string => {
  return (
    (rowData[ROW_DATA_KEYS.ID] as string) ??
    (rowData[TRACK_RECORD_DATA_STATE_KEYS.TRACK_EXISTING_ID] as string)
  );
};

export const isPOGroupIdRequired = (
  rowData: RowData,
  setupName: string,
  selectedTabSectionKey: string | undefined,
): boolean => {
  const PO_GROUP_TAB_KEY = "poGroupTab";

  if (
    setupName !== SCHEMA_TITLES.PO_CONFIGURATION_GROUP ||
    !selectedTabSectionKey ||
    selectedTabSectionKey === PO_GROUP_TAB_KEY
  ) {
    return false;
  }
  const isNewEntryRecord = isNewRecord(
    rowData[PO_GROUP_TAB_KEY] as RowDataObject,
  );
  const isPOGroupIdExist = Boolean(
    (rowData[PO_GROUP_TAB_KEY] as RowDataObject)?.id,
  );
  return isNewEntryRecord && !isPOGroupIdExist;
};

export const getPOConfigurationGroupGLStoreOptions = (
  selectedTabSectionKey: string | undefined,
  extendedAccountInfo: ExtendedAccountInfo[],
  rowData: RowData,
): ISelectableOption[] => {
  const SELECT_ASSIGN_COMPANIES: ISelectableOption = {
    label: "Select Assign Companies",
    value: "",
    disabled: true,
  };

  if (selectedTabSectionKey !== "poAccountsTab")
    return [SELECT_ASSIGN_COMPANIES];

  const storeOptionsFromDetailsAPIResponse =
    getStoreOptionsFromDetailsAPIResponse(extendedAccountInfo);

  const targetPropertyKey = "group_stores";
  const targetPropertySubKey = "store_id";

  const sectionData = rowData[selectedTabSectionKey] as RowData;
  const rowArray = sectionData?.[targetPropertyKey] as RowDataObject[];
  const targetSelectedStores = rowArray
    ?.filter((item) => item[targetPropertySubKey] !== "")
    ?.map((item) => item[targetPropertySubKey]);

  if (targetSelectedStores === undefined || targetSelectedStores.length === 0) {
    return [SELECT_ASSIGN_COMPANIES];
  }

  return storeOptionsFromDetailsAPIResponse.filter((item) =>
    targetSelectedStores.includes(item.value),
  );
};

const PO_TYPE_CODE_MAP: Record<string, string> = {
  Miscellaneous: "MISC",
  Sublet: "SBLT",
  "Part Invoice": "PRT",
  Replenishment: "REP",
  Vehicle: "VEH",
};

const getPODefaultTypes = (
  rowData: RowData,
  schema: JsonSchema,
): RowDataObject[] => {
  const poTypes =
    ((rowData.poTypesTab as RowData)?.poTypes as RowDataObject[]) ?? [];

  const template = poTypes[0] ?? {};

  const enumValues =
    ((schema.properties?.poTypes?.items as JsonSchemaItems)?.properties
      ?.po_type_category?.enum as PrimitiveValueType[]) ?? [];

  return enumValues.map((category) => ({
    ...template,
    po_type_category: category,
    po_type_desc: category,
    po_type_cd: PO_TYPE_CODE_MAP[category as string] ?? "",
  }));
};

export const getChartOfAccountsSubTypeOptions = (
  enumValues: PrimitiveValueType[],
  rowData: RowData,
  enumLabels?: EnumLabels,
): ISelectableOption[] => {
  const isAccountTypeSelected = rowData[ROW_DATA_KEYS.ACCOUNT_TYPE] as
    | string
    | null;

  if (!isAccountTypeSelected) {
    return [
      {
        label: "Select Account Type",
        value: "",
        disabled: true,
      },
    ];
  }

  return convertToOptions(enumValues, enumLabels);
};

export const getFilteredCashAccountOptions = (
  options: ISelectableOption[],
  rowData: RowData,
): ISelectableOption[] => {
  const valueToExclude = rowData[ROW_DATA_KEYS.CASH_CLEARING_ACCOUNT];

  if (!valueToExclude) return options;
  return options.filter((opt) => opt.value !== valueToExclude);
};

export const getFilteredCashClearingAccountOptions = (
  options: ISelectableOption[],
  rowData: RowData,
): ISelectableOption[] => {
  const valueToExclude = rowData[ROW_DATA_KEYS.CASH_ACCOUNT];

  if (!valueToExclude) return options;
  return options.filter((opt) => opt.value !== valueToExclude);
};

export const getFilteredInterCoTransferCompanyBStore = (
  options: ISelectableOption[],
  rowData: RowData,
): ISelectableOption[] => {
  const valueToExclude = rowData[ROW_DATA_KEYS.COMPANY_A_STORE];

  if (!valueToExclude) return options;
  return options.filter((opt) => opt.value !== valueToExclude);
};

export const getFilteredRemittanceFormMappingCashAccountOptions = (
  options: ISelectableOption[],
  rowDataList: RowData[],
): ISelectableOption[] => {
  const valuesToExclude = rowDataList.map(
    (item) => item[ROW_DATA_KEYS.CASH_ACCOUNT],
  );

  if (valuesToExclude.length > 0) {
    return options.filter((opt) => !valuesToExclude.includes(opt.value));
  }
  return options;
};

export const calculateTotal = ({
  inputValues,
  fieldKey,
}: {
  inputValues: RowDataObject[] | undefined;
  fieldKey: string;
}): number => {
  if (!inputValues || inputValues.length === 0) {
    return 0;
  }

  const total = inputValues.reduce((sum, item) => {
    const value = item[fieldKey];
    const num = Number(value);
    return Number.isFinite(num) ? sum + num : sum;
  }, 0);

  return Number(total.toFixed(2));
};

export const getGLCalendarIsPostingMonthActiveLabels = ({
  rowIndex,
  rowData,
  propertyKey,
}: {
  rowIndex?: number;
  rowData: RowData;
  propertyKey: string;
}): string => {
  if (rowIndex === undefined) return "";

  const rows = (rowData[propertyKey] as RowDataObject[] | undefined) ?? [];
  const activeIndex = rows.findIndex(
    ({ is_posting_month_active }) => is_posting_month_active,
  );

  return activeIndex === rowIndex ? "Active" : "Closed";
};

export const getARMigrationInfo = (
  rowDataList: RowData[],
): ActiveARRecordMigrationInfo | undefined => {
  const activeMigrationRow = rowDataList.find(
    (rowData) =>
      rowData[ROW_DATA_KEYS.AR_SWITCH_IN_PROGRESS] !== undefined &&
      rowData[ROW_DATA_KEYS.AR_SWITCH_IN_PROGRESS] !== null,
  ) as RowData | undefined;

  if (activeMigrationRow?.[ROW_DATA_KEYS.AR_SWITCH_IN_PROGRESS] !== true) {
    return undefined;
  }

  const { switchStatus, fromScheduleCode, toScheduleCode } = activeMigrationRow;

  return {
    switchStatus: switchStatus as string,
    tableMessage: `Note: Schedule Code conversion from ${fromScheduleCode} to ${toScheduleCode} is in progress.`,
  };
};

export const getInterCoTransferTargetCompanyGroupIdOptions = ({
  rowData,
  options,
  rowDataList,
}: {
  rowData: RowData;
  options: ISelectableOption[];
  rowDataList: RowData[];
}): ISelectableOption[] => {
  if (!isNewRecord(rowData)) return options;
  const sourceCompanyGroupId = String(
    rowData[ROW_DATA_KEYS.SOURCE_COMPANY_GROUP_ID] ?? "",
  );

  if (!sourceCompanyGroupId) {
    return [
      {
        label: "Select Company A GL Company",
        value: "",
        disabled: true,
      },
    ];
  }

  const filteredOptions = options.filter(({ value }) => {
    const targetCompanyGroupId = String(value);

    if (targetCompanyGroupId === sourceCompanyGroupId) {
      return false;
    }

    const selectedPair = [sourceCompanyGroupId, targetCompanyGroupId]
      .sort((a, b) => a.localeCompare(b))
      .join("-");

    const pairExists = rowDataList.some((row) => {
      const source = String(row[ROW_DATA_KEYS.SOURCE_COMPANY_GROUP_ID] ?? "");
      const target = String(row[ROW_DATA_KEYS.TARGET_COMPANY_GROUP_ID] ?? "");
      if (!source || !target) {
        return false;
      }
      return (
        [source, target].sort((a, b) => a.localeCompare(b)).join("-") ===
        selectedPair
      );
    });

    return !pairExists;
  });

  return filteredOptions.length
    ? filteredOptions
    : [NO_RESULTS_DEFAULT_IOPTION()];
};
